import { createConfigParametersRepository } from '../../../configParametersRepository';
import type {
  ConfigRepositoryParameters,
  GenericConfigParameters,
} from '../../../configParametersRepository/types';
import { get } from '../../../utils/httpRequest';
import { updateCustomParamsAndDimensions } from '../../customDimensionsRepository/storageSync';
import type { CustomDimensionsOrParams } from '../../google-analytics/ga4Types';
import {
  CUSTOM_PARAMS_STORAGE_KEY,
  USER_PROPERTIES_STORAGE_KEY,
} from '../../resources/constants';
import type { CustomDimensions } from '../../types';
import { SITE_TYPE } from './constants';
import { createRuntimeGoogleState } from './state';
import { getQueryParameterFromUrl } from './utils';

export const createDefaultDimensions = (
  response: CustomDimensionsOrParams,
  genericParameters: Partial<GenericConfigParameters>,
  buyPathDimension: string
): CustomDimensionsOrParams => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();
  let dimensions: CustomDimensionsOrParams = {
    lang_cod: genericParameters.locale,
    site_type: SITE_TYPE[genericParameters.flow || ''] || '',
    touchpoint:
      getQueryParameterFromUrl('referral', window.location.href) || '',
    full_referrer: document.referrer.substring(0, 1200),
    buypath: buyPathDimension,
  };

  if (genericParameters.isNativeInterface) {
    const { device } = genericParameters;
    dimensions = {
      ...dimensions,
      site_type: 'A_' + (device?.charAt(0) || ''),
      app_traffic: 'TRUE',
    };
  }

  const existingCustomDimensions = storageClient?.getTrackingStoredValue(
    CUSTOM_PARAMS_STORAGE_KEY
  ) as CustomDimensionsOrParams;

  return {
    ...existingCustomDimensions,
    ...response,
    ...dimensions,
  };
};

const parseWebsiteForMarketParam = (website: string) => {
  if (website?.length === 2) {
    return 'ED' + website;
  } else if (website?.length > 4) {
    return website.substring(0, 4);
  }
  return website;
};

export const updateCustomDimensions = (
  customDimensions: CustomDimensionsOrParams,
  genericParameters: Partial<GenericConfigParameters>
) => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();
  const savedUserProperties =
    storageClient?.getTrackingStoredValue(USER_PROPERTIES_STORAGE_KEY) || {};
  const defaultCustomDimensions = {
    ...savedUserProperties,
    ...customDimensions,
    market: parseWebsiteForMarketParam(genericParameters.website),
  };
  updateCustomParamsAndDimensions(defaultCustomDimensions);
};

// export const trackPostponedPageviews = async () => {
//   const { getPagesQueue } = createRuntimeGoogleState();
//   const pagesQueue = getPagesQueue();
//   const { trackPage } = await import('../../thirdPartyTrackingAgent/pages');
//   pagesQueue?.forEach((page) => void trackPage(page.page, page.options));
// };

export const setDefaultCustomDimensions = (
  genericParameters: Partial<GenericConfigParameters>,
  response: CustomDimensionsOrParams
) => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();
  const { setParameters } = createRuntimeGoogleState();
  const { buypath } =
    storageClient?.getTrackingStoredValue(CUSTOM_PARAMS_STORAGE_KEY) || {};
  const buyPathDimension = buypath || '';

  const customDimensions = createDefaultDimensions(
    response,
    genericParameters,
    buyPathDimension
  );
  updateCustomDimensions(customDimensions, genericParameters);

  setParameters({
    hasSetDefaultCustomDimensions: true,
  });
  // void trackPostponedPageviews();
};

export const getDefaultCustomDimensions = async (
  configParameters: ConfigRepositoryParameters
) => {
  const { endpoint } = configParameters.googleAnalytics;
  const { generic: genericParameters } = configParameters;
  const url = endpoint.defaultCustomDimensions;

  if (!url) {
    setDefaultCustomDimensions(genericParameters, {});
    return;
  }

  try {
    const response: CustomDimensions = await get(url);
    setDefaultCustomDimensions(genericParameters, response);
  } catch (_) {
    setDefaultCustomDimensions(genericParameters, {});
  }
};
